import {useCallback, useState} from 'react';
import type {PasswordSchema} from './useSchema';
import {
	useAgeSchema,
	useConsentSchema,
	useEmailSchema,
	useIdentitySchema,
	usePasswordSchema,
	useUsernameSchema,
} from './useSchema';
import {useTranslation} from './useTranslation';
import {useApiCall} from '@deezer/react-legacy-api';
import {isLegacyApiError, useLegacyApiError} from './useLegacyApiError';
import {useAutolog} from './auth/useAutolog';
import {AuthStatus} from './auth/useAutolog/utils';
import type {Schema} from 'yup';

export type EmailCheckApiResult = {
	domain_validity: boolean;
	availability: boolean;
	domain: string;
	suggestion?: string;
};
const isEmailValidationResponse = (obj: any): obj is EmailCheckApiResult =>
	typeof obj !== 'undefined' &&
	typeof obj.domain !== 'undefined' &&
	typeof obj.domain_validity !== 'undefined' &&
	typeof obj.availability !== 'undefined';

type EmailSuggestion =
	{type: 'suggestion'; suggestion: string} | {type: 'switch_to_login'};
type EmailError = {
	status: 'error';
	message: string;
	suggestion?: EmailSuggestion;
};
type EmailSuccess = {
	status: 'success';
	suggestion?: EmailSuggestion;
};

export type EmailValidatorResult = EmailSuccess | EmailError;
export const useDeezerBackendEmailValidation = (checkEmailUnique = false) => {
	const apiCall = useApiCall();
	const {status} = useAutolog();
	const [translateError] = useLegacyApiError();
	const t = useTranslation();

	const validateEmail = useCallback(
		async (value: string): Promise<EmailSuccess | EmailError> => {
			const valueBeforeValidation = value;
			try {
				if (status === AuthStatus.loading) {
					return {status: 'success'};
				}
				const response = await apiCall({
					method: 'deezer.emailCheck',
					data: {EMAIL: value},
				});
				// Value has changed since api call or api call was debounced
				if (!response || value !== valueBeforeValidation)
					return {status: 'success'};

				if (isEmailValidationResponse(response)) {
					// Invalid domain
					if (!response.domain_validity) {
						return {
							status: 'error',
							message: t('error.domainnnamenotallowed', {
								sprintf: [response.domain],
							}),
						};
					}
					// Email already used
					if (response.availability) {
						return {
							status: 'success',
							suggestion: response.suggestion
								? {
										type: 'suggestion',
										suggestion: response.suggestion,
									}
								: undefined,
						};
					} else {
						if (checkEmailUnique) {
							return {
								status: 'error',
								message: t('error.accountalrdyassociatedwithemail'),
								suggestion: {type: 'switch_to_login'},
							};
						}
						return {
							status: 'success',
							suggestion: {type: 'switch_to_login'},
						};
					}
				} else {
					return {
						status: 'success',
					};
				}
			} catch (e) {
				if (isLegacyApiError(e)) {
					return {
						status: 'error',
						message: translateError(e),
					};
				} else {
					return {
						status: 'success',
					};
				}
			}
		},
		[apiCall, t, checkEmailUnique, translateError, status],
	);
	return {validateEmail};
};

/**
 * @author lgalliot (Customer & Monetization)
 *
 * onChange validators based on yup schemas
 *
 * @see useValidators - ./useSchema.ts -> the yup
 *    schemas associated to the following validators callbacks.
 */
export const useEmailValidator = (checkEmailUnique = false) => {
	const [emailSchema] = useEmailSchema();
	const [suggestion, setSuggestion] = useState<EmailSuggestion | undefined>();
	const {validateEmail} = useDeezerBackendEmailValidation(checkEmailUnique);
	const validate = useCallback(
		async (email: string) => {
			email = email.trim();
			// Schema validation
			try {
				await emailSchema.validate(email);
			} catch (e: any) {
				setSuggestion(undefined);
				return e.message;
			}

			// Email backend rules validation
			const result = await validateEmail(email);
			setSuggestion(result.suggestion);
			if (result.status === 'error') {
				return result.message;
			}
		},
		[emailSchema, validateEmail],
	);
	return [
		validate,
		{suggestion, removeSuggestion: () => setSuggestion(undefined)},
	] as const;
};

export const useAgeValidator = () => {
	const t = useTranslation();
	const [ageSchema] = useAgeSchema();
	const validate = useCallback(
		async (value: number | string) => {
			value = typeof value === 'string' ? parseInt(value, 10) : value;

			// This might pose problem in the future - trying not to anticipate too much
			if (isNaN(value)) {
				return t('error.ageisinvalid');
			}
			try {
				await ageSchema.validate(value);
			} catch (e: any) {
				return e.message;
			}
		},
		[ageSchema, t],
	);
	return [validate] as const;
};

function useSimpleValidator(schema: Schema) {
	const validate = useCallback(
		async (value: string) => {
			try {
				await schema.validate(value);
			} catch (e: any) {
				return e.message;
			}
		},
		[schema],
	);
	return [validate] as const;
}

export function useTurkeyConsentValidator() {
	const [consentSchema] = useConsentSchema();
	return useSimpleValidator(consentSchema);
}

export const useUsernameValidator = () => {
	const [usernameSchema] = useUsernameSchema();
	return useSimpleValidator(usernameSchema);
};

export const useIdentityValidator = () => {
	const [identitySchema] = useIdentitySchema();
	return useSimpleValidator(identitySchema);
};

export const usePasswordValidator = (options?: PasswordSchema) => {
	const [pwdSchema] = usePasswordSchema(options);
	return useSimpleValidator(pwdSchema);
};
